import type { PropsWithChildren } from "react";
/* eslint complexity: ["error", {max:7}] -- this overrides for the entire file */
import { useEffect, useState } from "react";

import type { LayoutChangeEvent } from "react-native";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";

import { AnimationDuration } from "@/constants/UI";
import Box from "@/ui/layout/Box";
import { isWeb } from "@/utils/platform.util";

interface CollapsibleContainerProps extends PropsWithChildren {
  durationMs?: number;
  isExpanded: boolean;
  minHeight?: number;
}

/** Animates height between collapsed (minHeight) and expanded (content height). Measures children to derive the target height. */
const CollapsibleContainer = ({
  children,
  durationMs: duration = AnimationDuration.Default,
  isExpanded,
  minHeight = 0,
}: CollapsibleContainerProps) => {
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
  const animatedHeight = useSharedValue(minHeight);

  const onLayout = (event: LayoutChangeEvent) => {
    const layoutHeight = event.nativeEvent.layout.height;
    if (layoutHeight <= 0 || maxHeight === layoutHeight) {
      return;
    }
    setMaxHeight(layoutHeight);
  };

  // Handle animations when dependencies change
  useEffect(() => {
    if (maxHeight === undefined) return;

    const targetHeight = isExpanded ? maxHeight : minHeight;
    const currentHeight = animatedHeight.value;

    // Handle sudden layout changes (e.g., switching patient toggle with different data)
    // If current height doesn't match either min or max, snap to the target without animation
    if (currentHeight !== minHeight && currentHeight !== maxHeight) {
      animatedHeight.value = targetHeight;
      return;
    }

    // Animate to target height if there's a change
    if (currentHeight !== targetHeight) {
      animatedHeight.value = withTiming(targetHeight, { duration });
    }
  }, [isExpanded, minHeight, maxHeight, duration, animatedHeight]);

  // Read-only style computation - no mutations allowed here
  const collapsibleStyle = useAnimatedStyle(() => {
    return {
      height: animatedHeight.value,
    };
  }, [animatedHeight]);

  return (
    <Animated.View
      style={[collapsibleStyle, { overflow: "hidden" }]}
      testID="collapsible-container"
    >
      <Box
        onLayout={onLayout}
        // on web, width 100% needed to prevent partial clipping
        style={{ position: "absolute", width: isWeb() ? "100%" : undefined }}
        testID="collapsible-content"
      >
        {children}
      </Box>
    </Animated.View>
  );
};

export type { CollapsibleContainerProps };
export default CollapsibleContainer;
